import { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FileAccessMode, FileEncrypted } from "../../constants/enums";
import { Box, CircularProgress, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { DocumentCallback, OnPasswordCallback, PasswordResponse } from "react-pdf/dist/cjs/shared/types";

import CryptoJS from "crypto-js";
import AddIcon from "@mui/icons-material/Add";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import RotateLeftOutlinedIcon from "@mui/icons-material/RotateLeftOutlined";
import ReportGmailerrorredOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import AssetService from "../../services/asset/asset.service";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";

import "./styles.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface IPdfInfo {
    totalPages?: number;
    password?: string;
}

const pdfViewerOptions = {
    cMapUrl: "cmaps/",
    cMapPacked: true,
}

const decrypt = (encodeValue: string, key: string, IV: string) => {
    if (Helpers.isNullOrEmpty(encodeValue)) {
        return "";
    }

    const SECRECT_KEY_PARSED = CryptoJS.enc.Utf8.parse(key);
    const IV_PARSED = CryptoJS.enc.Utf8.parse(IV);

    const bytes = CryptoJS.AES.decrypt(encodeValue, SECRECT_KEY_PARSED, { iv: IV_PARSED });
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedData;
}

const isPartiallyInViewport = function (element: HTMLElement, visiblePercent: number) {
    let rect = element.getBoundingClientRect(),
        windowHeight = (window.innerHeight || document.documentElement.clientHeight);

    return !(
        Math.floor(100 - (((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100)) < visiblePercent ||
        Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < visiblePercent
    )
};

const PDFViewerContainer = () => {
    const Strings = useStrings();
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const fileIdParamName = "fileId";
    const fileId = urlParams.get(fileIdParamName) || urlParams.get(fileIdParamName.toLowerCase());

    const { aesKey, aesIV, userInfo } = useCommonComponentContext();
    const [pdfUrl, setPdfUrl] = useState<string>();
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [isPdfError, setIsPdfError] = useState(false);
    const [pdfInfo, setPdfInfo] = useState<IPdfInfo>({});

    const scales = [0.5, 0.7, 0.8, 0.9, 1, 1.2, 1.3, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.1, 2.4, 2.5, 2.6];
    const [scaleIndex, setScaleIndex] = useState<number>(scales.findIndex(scale => scale === 1));

    // INITIALIZE
    useEffect(() => {
        const loadAssetInfo = async (fileId: string) => {
            try {
                const assetInfo = await AssetService.getAssetInfo(fileId);
                let pdfUrl = Helpers.getFileAccessUrl(assetInfo.id);

                if (assetInfo.accessMode === FileAccessMode.Public) {
                    pdfUrl = assetInfo.accessUrl;
                }

                if (assetInfo.encrypted === FileEncrypted.Encrypted) {
                    const encryptedSecret = await AssetService.getAssetSecret(fileId);
                    const password = decrypt(encryptedSecret, aesKey, aesIV);
                    setPdfInfo(prev => ({ ...prev, password }));
                }

                setPdfUrl(pdfUrl);
                setIsPdfLoaded(true);
            } catch (error: any) {
                handleError(error)
            }
        }

        if (!Helpers.isNullOrEmpty(fileId) &&
            !Helpers.isNullOrEmpty(userInfo?.userProfile?.identityId)) {
            loadAssetInfo(fileId);
        }
    }, [fileId, userInfo])

    // TRACK ACTIVE PAGE
    useEffect(() => {
        if (pdfInfo.totalPages > 0) {
            const handleScoll = () => {
                const pageOne = 1;
                const pageOneElement = document.getElementById(`pdf-page-${pageOne}`);
                const visiblePercent = 80 - (scaleIndex * 2);

                // Page 1
                if (isPartiallyInViewport(pageOneElement, visiblePercent)) {
                    const active = document.getElementById("active-pdf-page");
                    active.textContent = pageOne.toString();
                    return;
                }

                // Page 2 -> n
                Array.from({ length: pdfInfo.totalPages }, (_, i) => i + 1).map(page => {
                    const element = document.getElementById(`pdf-page-${page}`);
                    const visiblePercent = 20 + scales[scaleIndex];

                    if (page !== pageOne && isPartiallyInViewport(element, visiblePercent)) {
                        const active = document.getElementById("active-pdf-page");
                        active.textContent = page.toString();
                    }
                    return null;
                })
            }
            document.addEventListener("scroll", handleScoll);
            return () => {
                document.removeEventListener("scroll", handleScoll);
            }
        }
    }, [pdfInfo, scaleIndex])

    const handleError = (error: any) => {
        if (Helpers.isDev()) {
            console.log(error);
        }
        setIsPdfError(true);
    }

    const handlePdfPassword = (callback: OnPasswordCallback, reason: PasswordResponse) => {
        if (Helpers.isNullOrEmpty(pdfInfo?.password)) {
            setIsPdfLoaded(false);
        } else {
            callback(pdfInfo.password)
        }
    }

    const handlePdfLoadSuccess = (documentCallback: DocumentCallback) => {
        setPdfInfo(prev => ({ ...prev, totalPages: documentCallback.numPages }))
    }

    const handleZoomIn = () => {
        if (scales[scaleIndex + 1]) {
            setScaleIndex(index => index + 1)
        }
    }

    const handleZoomOut = () => {
        if (scales[scaleIndex - 1]) {
            setScaleIndex(index => index - 1)
        }
    }

    const handleResetZoom = () => {
        setScaleIndex(scales.findIndex(scale => scale === 1))
    }

    const handleChangeScale = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const index = scales.findIndex(scale => scale.toString() === value);
        if (index > -1) {
            setScaleIndex(index);
        }
    }

    return (
        <Box sx={{
            background: "#2A2A2E",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            {isPdfLoaded ? (<>
                <Grid
                    container
                    spacing={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        background: "#38383D",
                        width: "100%",
                        zIndex: 9999,
                        position: "fixed",
                        top: 0,
                        py: 0.2
                    }}
                >
                    {/* CURRENT VIEWING PAGE */}
                    <Grid item xs={4} display="flex" justifyContent="start" alignItems="center">
                        {!Helpers.isNullOrEmpty(pdfInfo?.totalPages) && (<>
                            <Typography sx={{ ml: 0.5, fontSize: "1rem", color: "#fff", fontWeight: "bold" }}>
                                {`${Strings.Common.PAGE}:`}
                            </Typography>
                            <Typography id="active-pdf-page" sx={{ mx: 0.5, fontSize: "1rem", color: "#fff", fontWeight: "bold" }}>
                                {"1"}
                            </Typography>
                            <Typography sx={{ fontSize: "1rem", color: "#fff", fontWeight: "bold" }}>
                                {`/ ${pdfInfo.totalPages}`}
                            </Typography>
                        </>)}
                    </Grid>

                    {/* ACTION BUTTONS */}
                    <Grid item xs={4} display="flex" justifyContent="center" alignItems="center">
                        <Tooltip title={Strings.Common.ZOOM_OUT} placement="bottom" arrow>
                            <IconButton
                                size="small"
                                style={{ color: "#fff" }}
                                onClick={handleZoomOut}
                            >
                                <RemoveOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={Strings.Common.ZOOM_IN} placement="bottom" arrow>
                            <IconButton
                                size="small"
                                style={{ color: "#fff" }}
                                onClick={handleZoomIn}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={"Reset"} placement="bottom" arrow>
                            <IconButton
                                size="small"
                                style={{ color: "#fff" }}
                                onClick={handleResetZoom}
                            >
                                <RotateLeftOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ fontSize: "1rem", color: "#fff" }}>
                            <select value={scales[scaleIndex]} onChange={handleChangeScale}>
                                {scales.map(scale => (
                                    scale.toString().length > 3 ? (
                                        <option key={scale.toString()} value={scale}>
                                            {"Page width"}
                                        </option>
                                    ) : (
                                        <option key={scale.toString()} value={scale}>
                                            {`${scale * 100}%`}
                                        </option>
                                    )
                                ))}
                            </select>
                        </Box>
                    </Grid>

                    {/* DUMMY SPACE */}
                    <Grid item xs={4}>
                    </Grid>
                </Grid>

                <Document
                    renderMode="canvas"
                    externalLinkTarget="_blank"
                    file={pdfUrl}
                    options={pdfViewerOptions}
                    onPassword={handlePdfPassword}
                    onLoadSuccess={handlePdfLoadSuccess}
                    onLoadError={handleError}
                    loading={<PdfLoadingStatus type="loading" />}
                    error={<PdfLoadingStatus type="error" />}
                >
                    {Array.from({ length: pdfInfo.totalPages }, (_, i) => i + 1).map(page => (
                        <Box
                            key={page.toString()}
                            id={`pdf-page-${page}`}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: page < pdfInfo.totalPages ? 1.3 : 0
                            }}
                        >
                            <Page
                                pageNumber={page}
                                renderTextLayer={false}
                                scale={scales[scaleIndex]}
                            />
                        </Box>
                    ))}
                </Document>
            </>) : (
                isPdfError ? (
                    <PdfLoadingStatus type="error" />
                ) : (
                    <PdfLoadingStatus type="loading" />
                )
            )}
        </Box>
    )
}

const PdfLoadingStatus = ({ type }: { type?: "loading" | "error" }) => {
    const Strings = useStrings();
    return (
        <Box sx={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            <Box style={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                {(type === "loading" || Helpers.isNullOrEmpty(type)) ? (<>
                    <CircularProgress
                        size={20}
                        sx={{ mr: 1.5 }}
                        style={{ color: "#fff" }}
                    />
                    {Strings.Common.LOADING_PDF}
                </>) : (<>
                    <ReportGmailerrorredOutlinedIcon />
                    {Strings.Common.LOAD_PDF_ERROR}
                </>)}
            </Box>
        </Box>
    )
}

export default PDFViewerContainer